import { platformMeta, platformOrder } from '../platformMeta';
import type { CalendarPost, SerializedSocialChannel, SocialPlatform } from '../types';
import { atMinutes } from './calendarDates';

/** A free channel slot of a day: a connected channel with no post that day, at its best time. */
export interface CalendarFreeSlot {
  platform: SocialPlatform;
  at: Date;
  /** The time comes from the channel (`bestTimeUtc`), not from the platform hour. */
  isChannelBestTime: boolean;
}

/** A channel that can carry a post: it has a handle or a login. */
export function isUsableChannel(channel: SerializedSocialChannel): boolean {
  return (channel.handle !== null && channel.handle !== '') || channel.status === 'connected';
}

/** The best time of a channel on a day: its `bestTimeUtc`, else the usual hour of the platform. */
function slotOn(day: Date, channel: SerializedSocialChannel): { at: Date; isChannelBestTime: boolean } {
  if (channel.bestTimeUtc !== null && channel.bestTimeUtc !== '') {
    const [hours, minutes] = channel.bestTimeUtc.split(':').map((part) => Number(part));
    const at = new Date(day);
    at.setUTCHours(
      hours === undefined || Number.isNaN(hours) ? 12 : hours,
      minutes === undefined || Number.isNaN(minutes) ? 0 : minutes,
      0,
      0
    );
    // The UTC hour can fall on the day before or after; keep the local day.
    const local = atMinutes(day, at.getHours() * 60 + at.getMinutes());
    return { at: local, isChannelBestTime: true };
  }
  return { at: atMinutes(day, platformMeta[channel.platform].defaultHour * 60), isChannelBestTime: false };
}

/**
 * The free slots of a day (design b7 and the Week toggle of a2): one per usable channel with no
 * post on the day, in the platform order. A slot in the past is not free.
 */
export function freeSlotsOfDay(
  day: Date,
  channels: SerializedSocialChannel[],
  dayPosts: CalendarPost[],
  now: Date = new Date()
): CalendarFreeSlot[] {
  const taken = new Set(dayPosts.filter((post) => post.status !== 'canceled').map((post) => post.platform));
  return platformOrder
    .map((platform) => channels.find((channel) => channel.platform === platform))
    .filter((channel): channel is SerializedSocialChannel => channel !== undefined && isUsableChannel(channel))
    .filter((channel) => !taken.has(channel.platform))
    .map((channel) => ({ platform: channel.platform, ...slotOn(day, channel) }))
    .filter((slot) => slot.at.getTime() > now.getTime());
}
